import { useDispatch } from 'react-redux';
import React, { useCallback, useEffect, useRef } from 'react';
import { path, pick, prop } from 'ramda';
import { doDeposit } from '../actions';
import { getLocationParams, getURLParams } from '@core/js/utils/url.util';
import {
    INITIATORS,
    POST_MESSAGE_ERRORS
} from '@core/js/components/main/QuickDeposit/constants';
import { useShowNotification } from 'js/hooks/useShowNotification';
import { useConfirmationPopup } from '@core/js/components/common/popups/ConfirmationPopup';
import { performRedirect } from 'js/components/main/QuickDeposit/utils/redirect';
import { useAppSelector, useDeviceSelector, useUserSelector } from '@core/js/store/hooks';
import { usePortletConfig, usePortletSelector, usePortletTranslations } from '@core/js/components/system/Portlet';
import { useMethodSelector } from 'js/components/main/QuickDeposit/withCashierPortlet';
import { dispatcher } from 'dispatcher';
import { clearUsedPromoCodeFromStore } from '@core/js/utils/promocode.util';
import { getScript } from '@core/js/utils/scripts.util';
import { uniqueId } from '@core/js/utils/methods.util';
import {
    handle3dSecure,
    errorDeposit,
    handleTransactionStatus,
    prepareDepositRequest
} from 'js/components/main/QuickDeposit/Deposit/withDeposit';

export const handleRedirectMessage = async (redirectResponse, depositResponse, options) => {
    if (!redirectResponse) {
        return;
    }

    const { data: { headerParameters } } = depositResponse;
    const is3DSecure = Boolean(path(['X-Redirect-Type', '0'], headerParameters));
    const { error, href = window.location.href } = redirectResponse;
    const { search } = new window.URL(href, document.baseURI);
    const urlParams = getURLParams(search);
    const notifyData = error ? { status: 'error', initiator: INITIATORS.CASHIER_QD } : urlParams;

    if (is3DSecure && error !== POST_MESSAGE_ERRORS.LIGHTBOX_CLOSED) {
        return await handle3dSecure({ error, ...urlParams }, headerParameters, options);
    }

    return await handleTransactionStatus(notifyData, options);
};

export const getDataTransData = (data) => {
    const urlParams = getURLParams(data.headerParameters?.['X-Redirect-Content']?.[0]);

    const formID = uniqueId('datatrans-');
    const formDT = document.createElement('form');
    const optionsDT = {
        form: `#${formID}`,
        closed: () => {
            document.body.removeChild(formDT);
        }
    };

    formDT.id = formID;

    Object.keys(urlParams).forEach((key) => {
        formDT.setAttribute(key, urlParams[key]);
    });
    return { formDT, optionsDT };
};

export const handleDataTransDeposit = async (scriptURL, formDT, optionsDT, cachedDefine) => {
    try {
        await getScript(scriptURL);
        window.define = cachedDefine;
        document.body.appendChild(formDT);
        window.Datatrans?.startPayment?.(optionsDT);
    } catch (error) {
        console.error(error);
    }
};

export const successDeposit = async (response, options, extWindow) => {
    const { popupsDispatch, redirectType } = options;

    if (response.data.headerParameters) {
        switch (redirectType) {

            case 'lightboxdt': {
                (async () => {
                    const scriptURL = response.data.headerParameters?.Location?.[0];

                    if (!scriptURL) {
                        console.error(scriptURL, 'No scriptURL');
                    }

                    const { formDT, optionsDT } = getDataTransData(response.data);

                    // hack for resolving conflict with window.define in DataTrans script
                    const cachedDefine = window.define;
                    window.define = null;
                    await handleDataTransDeposit(scriptURL, formDT, optionsDT, cachedDefine);
                })();

                break;
            }

            default: {
                (async () => {
                    try {

                        const result = await performRedirect(response, { redirectType, extWindow: extWindow.current },
                            popupsDispatch);
                        await handleRedirectMessage(result, response, options);

                        if (result) {
                            await handleRedirectMessage(result, response, options);
                        }

                    } catch (error) {
                       console.error(error);
                    }
                })();
            }
        }

        return;
    }

    // for public event
    dispatcher.dispatch('deposit:success', response.data);
};

export default Wrapped => (props) => {
    const { afterSuccessDeposit } = props;
    const method = useMethodSelector();
    const { initiator } = usePortletConfig();
    const isQuickDeposit = usePortletSelector(prop('isQuickDeposit'));
    const clientPlatform = useDeviceSelector(prop('clientPlatform'));
    const { themeName, cashierClientType, sendCashierMessagesToGS } = useAppSelector(pick([
        'themeName',
        'cashierClientType',
        'sendCashierMessagesToGS'
    ]));
    const { clientIp, storeCardConfirmationRequired } = useUserSelector(pick([
        'clientIp',
        'storeCardConfirmationRequired'
    ]));
    const showNotification = useShowNotification();
    const showConfirmationPopup = useConfirmationPopup();
    const dispatch = useDispatch();
    const t = usePortletTranslations();
    const extWindow = useRef();
    const depositRef = useRef();
    const { clientType, clientPlatform: clientPlatformUrl } = getLocationParams();

    const deposit = useCallback(async (data) => {
        const { promoCode } = data;
        const { redirectType } = method;

        if (redirectType === 'popup') {
            extWindow.current = window.open('', '', 'resizable=yes, width=800px, height=600px');
        }

        const depositParams = {
            ...method,
            afterSuccessDeposit,
            initiator,
            isQuickDeposit,
            themeName,
            cashierClientType,
            sendCashierMessagesToGS,
            clientIp,
            storeCardConfirmationRequired,
            ...clientType && { clientType },
            ...clientPlatformUrl && { clientPlatform: clientPlatformUrl }
        };

        const successDepositOptions = {
            popupsDispatch: dispatch,
            redirectType,
            clientPlatform,
            showNotification,
            clientIp,
            t
        };

        try {
            const depositResult = await doDeposit(prepareDepositRequest(depositParams)(data));
            await successDeposit(depositResult, successDepositOptions, extWindow);
            afterSuccessDeposit?.();
            clearUsedPromoCodeFromStore(promoCode);
        } catch (err) {
            extWindow.current?.close();
            return errorDeposit({ data, depositRef, showConfirmationPopup, t })(err);
        }
    }, [
        clientType,
        clientPlatformUrl,
        method,
        afterSuccessDeposit,
        initiator,
        isQuickDeposit,
        themeName,
        cashierClientType,
        sendCashierMessagesToGS,
        clientIp,
        storeCardConfirmationRequired,
        dispatch,
        clientPlatform,
        showNotification,
        t,
        showConfirmationPopup
    ]);

    depositRef.current = deposit;

    // withTransactionStatusOnInit
    useEffect(() => {
        const params = getURLParams(window.location.search);

        if ('status' in params) {
            handleTransactionStatus(params, { t, clientPlatform, showNotification }).then(afterSuccessDeposit);
        }
    }, [afterSuccessDeposit, clientPlatform, showNotification, t]);

    return <Wrapped {...props} deposit={deposit} />;
};
